import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Minus, Plus, Star, ChevronLeft, ChevronRight, Check, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImage, setCurrentImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [reviewForm, setReviewForm] = useState({ customer_name: '', rating: 5, comment: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, reviewsRes] = await Promise.all([
          fetch(`${API}/products/${id}`),
          fetch(`${API}/products/${id}/reviews`)
        ]);
        if (!productRes.ok) throw new Error('Product not found');
        const productData = await productRes.json();
        const reviewsData = await reviewsRes.json();
        setProduct(productData);
        setReviews(reviewsData);
        setSelectedSize(productData.sizes[0]);
        setSelectedColor(productData.colors[0]);
        document.title = `${productData.name} | Crookid Smilez`;
      } catch (error) {
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) { toast.error('Please select size and color'); return; }
    addToCart({ product_id: product.id, name: product.name, price: product.price, size: selectedSize, color: selectedColor, quantity, image: product.images[0] });
    toast.success(`${product.name} added to cart`);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/products/${id}/reviews`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewForm)
      });
      if (res.ok) {
        const newReview = await res.json();
        setReviews([newReview, ...reviews]);
        setReviewForm({ customer_name: '', rating: 5, comment: '' });
        toast.success('Review submitted!');
      }
    } catch (error) { toast.error('Error submitting review'); }
  };

  const averageRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-2 border-cs-ochre border-t-transparent animate-spin" />
    </div>
  );

  if (!product) return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-anton text-4xl uppercase text-cs-text mb-4">Product Not Found</h1>
        <Link to="/shop" className="font-mono text-sm text-cs-ochre uppercase tracking-wider hover:underline">Back to Shop</Link>
      </div>
    </div>
  );

  return (
    <div className="fade-in" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-cs-dark border-b border-cs-border py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-xs text-cs-muted uppercase tracking-wider hover:text-cs-ochre transition-colors" data-testid="back-to-shop">
            <ArrowLeft className="w-4 h-4" /> Back to Shop
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Images */}
          <div className="space-y-3" data-testid="product-images">
            <div className="relative aspect-square bg-cs-card border border-cs-border overflow-hidden">
              <img src={product.images[currentImage]} alt={product.name} className="w-full h-full object-cover" />
              {product.images.length > 1 && (
                <>
                  <button onClick={() => setCurrentImage(prev => prev === 0 ? product.images.length - 1 : prev - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-cs-bg/80 border border-cs-border text-cs-text hover:bg-cs-olive transition-colors" data-testid="prev-image">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={() => setCurrentImage(prev => prev === product.images.length - 1 ? 0 : prev + 1)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-cs-bg/80 border border-cs-border text-cs-text hover:bg-cs-olive transition-colors" data-testid="next-image">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img, i) => (
                <button key={`thumb-${img}`} onClick={() => setCurrentImage(i)} className={`w-20 h-20 flex-shrink-0 border ${currentImage === i ? 'border-cs-ochre' : 'border-cs-border'} overflow-hidden`} data-testid={`thumbnail-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div data-testid="product-info">
            <p className="font-mono text-xs uppercase text-cs-ochre tracking-[0.3em] mb-2">{product.category}</p>
            <h1 className="font-anton text-3xl md:text-4xl uppercase text-cs-text tracking-tight mb-3" data-testid="product-name">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`rating-star-${i}`} className={`w-4 h-4 ${i < Math.round(averageRating) ? 'fill-cs-ochre text-cs-ochre' : 'text-cs-border'}`} />
                ))}
              </div>
              <span className="font-mono text-xs text-cs-muted">({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})</span>
            </div>

            <p className="font-mono text-3xl text-cs-ochre font-bold mb-4" data-testid="product-price">${product.price.toFixed(2)}</p>
            <p className="font-mono text-sm text-cs-muted leading-relaxed mb-8">{product.description}</p>

            {/* Color */}
            <div className="mb-6">
              <p className="font-mono text-sm text-cs-text uppercase tracking-wider mb-3">Color: <span className="text-cs-ochre">{selectedColor}</span></p>
              <div className="flex gap-2">
                {product.colors.map(color => (
                  <button key={color} onClick={() => setSelectedColor(color)} className={`w-10 h-10 border flex items-center justify-center ${selectedColor === color ? 'border-cs-ochre' : 'border-cs-border'}`} style={{ backgroundColor: color.toLowerCase() === 'yellow' ? '#D97706' : color.toLowerCase() }} data-testid={`color-${color}`}>
                    {selectedColor === color && <Check className={`w-5 h-5 ${color.toLowerCase() === 'black' ? 'text-white' : 'text-black'}`} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="mb-6">
              <p className="font-mono text-sm text-cs-text uppercase tracking-wider mb-3">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(size => (
                  <button key={size} onClick={() => setSelectedSize(size)} className={`min-w-[3rem] py-2 px-4 font-mono text-xs border transition-colors ${selectedSize === size ? 'bg-cs-ochre text-cs-dark border-cs-ochre' : 'border-cs-border text-cs-muted hover:border-cs-olive2'}`} data-testid={`size-${size}`}>
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <p className="font-mono text-sm text-cs-text uppercase tracking-wider mb-3">Quantity</p>
              <div className="flex items-center border border-cs-border w-fit">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="p-3 text-cs-muted hover:text-cs-text hover:bg-cs-bg transition-colors" data-testid="decrease-qty"><Minus className="w-4 h-4" /></button>
                <span className="w-14 text-center font-mono text-cs-text" data-testid="quantity">{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className="p-3 text-cs-muted hover:text-cs-text hover:bg-cs-bg transition-colors" data-testid="increase-qty"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Actions */}
            {product.shopify_url ? (
              <div className="space-y-3">
                <a href={product.shopify_url} target="_blank" rel="noopener noreferrer" className="w-full bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm py-4 hover:bg-cs-olive2 transition-colors flex items-center justify-center gap-2" data-testid="buy-on-shopify-btn">
                  <ShoppingBag className="w-4 h-4" /> Buy on Shopify - ${(product.price * quantity).toFixed(2)}
                </a>
                <button onClick={handleAddToCart} className="w-full border border-cs-border text-cs-muted font-mono uppercase tracking-wider text-sm py-4 hover:border-cs-ochre hover:text-cs-ochre transition-colors" data-testid="add-to-cart-btn">
                  Add to Cart
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <button onClick={handleAddToCart} className="w-full bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm py-4 hover:bg-cs-olive2 transition-colors flex items-center justify-center gap-2" data-testid="add-to-cart-btn">
                  <ShoppingBag className="w-4 h-4" /> Add to Cart - ${(product.price * quantity).toFixed(2)}
                </button>
                <Link to="/cart" onClick={handleAddToCart} className="w-full block border border-cs-border text-cs-muted font-mono uppercase tracking-wider text-sm py-4 hover:border-cs-ochre hover:text-cs-ochre transition-colors text-center" data-testid="buy-now-btn">
                  Buy Now
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16 border-t border-cs-border pt-10" data-testid="product-tabs">
          <div className="flex border-b border-cs-border">
            {['description', 'size-chart', 'reviews'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`font-mono text-xs uppercase tracking-wider py-4 px-6 border-b-2 -mb-[1px] transition-colors ${activeTab === tab ? 'border-cs-ochre text-cs-ochre' : 'border-transparent text-cs-muted hover:text-cs-ochre'}`} data-testid={`tab-${tab}`}>
                {tab.replace('-', ' ')}
              </button>
            ))}
          </div>

          <div className="py-8">
            {activeTab === 'description' && (
              <div data-testid="tab-content-description">
                <h3 className="font-anton text-xl uppercase text-cs-text mb-4">Details</h3>
                <p className="font-mono text-sm text-cs-muted leading-relaxed mb-4">{product.description}</p>
                {product.material && <><h4 className="font-mono text-sm text-cs-text uppercase tracking-wider mt-6 mb-2">Material</h4><p className="font-mono text-sm text-cs-muted">{product.material}</p></>}
                {product.care_instructions && <><h4 className="font-mono text-sm text-cs-text uppercase tracking-wider mt-6 mb-2">Care</h4><p className="font-mono text-sm text-cs-muted">{product.care_instructions}</p></>}
              </div>
            )}

            {activeTab === 'size-chart' && (
              <div data-testid="tab-content-size-chart">
                <h3 className="font-anton text-xl uppercase text-cs-text mb-4">Size Chart</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-cs-border font-mono text-sm">
                    <thead className="bg-cs-olive text-cs-text">
                      <tr>
                        <th className="border border-cs-border p-3">Size</th><th className="border border-cs-border p-3">Chest (in)</th><th className="border border-cs-border p-3">Length (in)</th><th className="border border-cs-border p-3">Sleeve (in)</th>
                      </tr>
                    </thead>
                    <tbody className="text-cs-muted">
                      <tr><td className="border border-cs-border p-3 text-center">S</td><td className="border border-cs-border p-3 text-center">36-38</td><td className="border border-cs-border p-3 text-center">27</td><td className="border border-cs-border p-3 text-center">33</td></tr>
                      <tr className="bg-cs-card"><td className="border border-cs-border p-3 text-center">M</td><td className="border border-cs-border p-3 text-center">38-40</td><td className="border border-cs-border p-3 text-center">28</td><td className="border border-cs-border p-3 text-center">34</td></tr>
                      <tr><td className="border border-cs-border p-3 text-center">L</td><td className="border border-cs-border p-3 text-center">40-42</td><td className="border border-cs-border p-3 text-center">29</td><td className="border border-cs-border p-3 text-center">35</td></tr>
                      <tr className="bg-cs-card"><td className="border border-cs-border p-3 text-center">XL</td><td className="border border-cs-border p-3 text-center">42-44</td><td className="border border-cs-border p-3 text-center">30</td><td className="border border-cs-border p-3 text-center">36</td></tr>
                      <tr><td className="border border-cs-border p-3 text-center">2XL</td><td className="border border-cs-border p-3 text-center">44-46</td><td className="border border-cs-border p-3 text-center">31</td><td className="border border-cs-border p-3 text-center">37</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div data-testid="tab-content-reviews">
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h3 className="font-anton text-xl uppercase text-cs-text mb-6">Reviews ({reviews.length})</h3>
                    {reviews.length === 0 ? (
                      <p className="font-mono text-sm text-cs-muted">No reviews yet. Be the first!</p>
                    ) : (
                      <div className="space-y-4">
                        {reviews.map(review => (
                          <div key={review.id} className="bg-cs-card border border-cs-border p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex">{[...Array(5)].map((_, i) => <Star key={`review-star-${i}`} className={`w-3.5 h-3.5 ${i < review.rating ? 'fill-cs-ochre text-cs-ochre' : 'text-cs-border'}`} />)}</div>
                              <span className="font-mono text-xs text-cs-text uppercase tracking-wider">{review.customer_name}</span>
                            </div>
                            <p className="font-mono text-xs text-cs-muted leading-relaxed">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-anton text-xl uppercase text-cs-text mb-6">Write a Review</h3>
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                      <div>
                        <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Name</label>
                        <input type="text" value={reviewForm.customer_name} onChange={e => setReviewForm({ ...reviewForm, customer_name: e.target.value })} required className="w-full bg-cs-bg border border-cs-border text-cs-text font-mono text-sm p-3 focus:outline-none focus:border-cs-ochre placeholder:text-cs-muted/50" data-testid="review-name" />
                      </div>
                      <div>
                        <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Rating</label>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button key={star} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: star })} className="p-1" data-testid={`rating-${star}`}>
                              <Star className={`w-7 h-7 ${star <= reviewForm.rating ? 'fill-cs-ochre text-cs-ochre' : 'text-cs-border'}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Review</label>
                        <textarea value={reviewForm.comment} onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })} required rows={4} className="w-full bg-cs-bg border border-cs-border text-cs-text font-mono text-sm p-3 focus:outline-none focus:border-cs-ochre placeholder:text-cs-muted/50" data-testid="review-comment" />
                      </div>
                      <button type="submit" className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-6 py-3 hover:bg-cs-olive2 transition-colors" data-testid="submit-review">Submit Review</button>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
